/**
 * Cierre de caja en dos pasos, con CONTEO A CIEGAS (spec 11, enmienda 2026-10-09).
 *  1. El cajero escribe lo que contó en cada método (efectivo con el fondo; el punto por lote). No ve lo que espera el
 *     sistema. Al declarar, queda guardado y no se cambia.
 *  2. Si cuadra, la caja queda cerrada. Si no, aparecen las diferencias: se justifican y, sobre el umbral, las
 *     autoriza un administrador o la dueña (clave o a distancia). Volver a contar lo permite quien autoriza cierres.
 * Se usa en Cierres y en la página Caja.
 */
import { formatearTasa, METODO_PAGO_ETIQUETA, METODO_PAGO_MONEDA, Money, type ArqueoDTO, type ArqueoMetodoDTO, type CierreSesionDTO, type ConteoCajaDTO, type MetodoPago, type ReporteCierreDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Plus, Printer, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useAutorizacion } from '@/componentes/Autorizacion.tsx';
import { mostrarRegistrado } from '@/componentes/Registrado.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Textarea } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { ErrorApi, get, mensajeDeError, post } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { useVeTasa2 } from '@/lib/tasa2.ts';
import { formatearFechaHora } from '@/lib/utils.ts';

const usd = (v: string | null | undefined) => (v === null || v === undefined ? '—' : `$ ${Money.parse(v).toDisplay()}`);
const simbolo = (moneda: string) => (moneda === 'BS' ? 'Bs' : '$');
const etiqueta = (m: string) => METODO_PAGO_ETIQUETA[m as MetodoPago] ?? m;

/** "1.234,56" o "1234.56" → Money; null si no es un número válido. */
export function leerMonto(v: string): Money | null {
  const s = v.trim();
  if (!s) return null;
  const normal = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  try {
    const m = Money.parse(normal);
    return m.esNegativo() ? null : m;
  } catch {
    return null;
  }
}

/** Filas del arqueo por método; los cierres guardados antes del 24-09-2026 solo tienen efectivo. */
export function filasArqueo(a: ArqueoDTO): ArqueoMetodoDTO[] {
  if (a.por_metodo) return a.por_metodo;
  return (['efectivo_usd', 'efectivo_bs'] as const).map((m) => {
    const fondo = m === 'efectivo_usd' ? a.fondo_usd : a.fondo_bs;
    const sistema = m === 'efectivo_usd' ? a.sistema_efectivo_usd : a.sistema_efectivo_bs;
    const decl = m === 'efectivo_usd' ? a.declarado_efectivo_usd : a.declarado_efectivo_bs;
    const s = Money.parse(sistema);
    const f = Money.parse(fondo);
    return { metodo: m, moneda: METODO_PAGO_MONEDA[m], fondo, sistema, sistema_sin_fondo: s.sub(f).toDocumento(), declarado: decl, declarado_sin_fondo: decl ? Money.parse(decl).sub(f).toDocumento() : null, diferencia: decl ? Money.parse(decl).sub(s).toDocumento() : null, diferencia_usd: null };
  });
}

export function Reporte({ r, tasaBs }: { r: ReporteCierreDTO; tasaBs?: string }) {
  // La tasa 2 al cierre solo la ve la dueña (pedido de la dueña 2026-10-19)
  const veTasa = useVeTasa2();
  return (
    <div className="space-y-2 text-sm" data-testid={`reporte-${r.tipo}`}>
      <div className="grid grid-cols-4 gap-2 rounded-lg bg-muted/60 p-2">
        <div><p className="text-xs text-muted-foreground">Documentos</p><p className="num font-semibold">{r.documentos}</p></div>
        {/* Lo fiscal en Bs, como va al libro (hallazgo B5); los cierres viejos solo traen los $ */}
        {/* El exento en Bs va bajo la base, como la columna del libro de ventas (re-auditoría 2026-10-18, ronda 2, hallazgo 5-H6) */}
        <div><p className="text-xs text-muted-foreground">Base</p><p className="num font-semibold">{usd(r.base)}</p>{r.base_bs ? <p className="num text-[11px] text-muted-foreground" data-testid={`reporte-${r.tipo}-base-bs`}>Bs {Money.parse(r.base_bs).toDisplay()}</p> : null}{r.exento_bs ? <p className="num text-[11px] text-muted-foreground" data-testid={`reporte-${r.tipo}-exento-bs`}>exento Bs {Money.parse(r.exento_bs).toDisplay()}</p> : null}</div>
        <div><p className="text-xs text-muted-foreground">IVA</p><p className="num font-semibold">{usd(r.iva)}</p>{r.iva_bs ? <p className="num text-[11px] text-muted-foreground">Bs {Money.parse(r.iva_bs).toDisplay()}</p> : null}</div>
        <div><p className="text-xs text-muted-foreground">Total</p><p className="num font-semibold">{usd(r.total)}</p><p className="num text-[11px] text-muted-foreground">Bs {Money.parse(r.total_bs).toDisplay()}</p></div>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Método</TableHead><TableHead className="text-right">Monto</TableHead><TableHead className="text-right">$ equiv.</TableHead><TableHead className="text-right">Ops.</TableHead></TableRow></TableHeader>
        <TableBody>
          {r.por_metodo.map((m) => (
            <TableRow key={m.metodo}><TableCell>{etiqueta(m.metodo)}{m.metodo === 'saldo_favor' || m.metodo === 'cambio' ? <span className="block text-[11px] text-muted-foreground">no es dinero que entra: no suma a lo cobrado</span> : null}</TableCell><TableCell className="text-right num">{m.moneda === 'BS' ? 'Bs ' : '$ '}{Money.parse(m.monto).toDisplay()}</TableCell><TableCell className="text-right num">{usd(m.monto_usd)}</TableCell><TableCell className="text-right num">{m.operaciones}</TableCell></TableRow>
          ))}
          <TableRow className="font-semibold"><TableCell>{r.vuelto_total_usd && Money.parse(r.vuelto_total_usd).esPositivo() ? 'Recibido (con el vuelto)' : 'Cobrado'}</TableCell><TableCell /><TableCell className="text-right num" data-testid={`reporte-${r.tipo}-cobrado`}>{usd(r.cobrado_usd)}</TableCell><TableCell /></TableRow>
          {/* Lo entregado de más y devuelto no es dinero cobrado (hallazgo B2) */}
          {r.vuelto_total_usd && Money.parse(r.vuelto_total_usd).esPositivo() ? <TableRow data-testid={`reporte-${r.tipo}-neto`}><TableCell>Vuelto entregado · <b>cobrado sin el vuelto</b></TableCell><TableCell className="text-right num">−{usd(r.vuelto_total_usd)}</TableCell><TableCell className="text-right num font-semibold">{usd(Money.parse(r.cobrado_usd).sub(r.vuelto_total_usd).toDocumento())}</TableCell><TableCell /></TableRow> : null}
          {r.igtf_usd && Money.parse(r.igtf_usd).esPositivo() ? <TableRow data-testid={`reporte-${r.tipo}-igtf`}><TableCell>IGTF percibido<span className="block text-[11px] text-muted-foreground">{r.tipo === 'completo' ? 'el del reporte fiscal más el de los anticipos anteriores al 19/10/2026; ' : ''}incluye el de facturas pagadas con saldo a favor (ese dinero entró como abono)</span></TableCell><TableCell className="text-right num">{r.igtf_bs ? `Bs ${Money.parse(r.igtf_bs).toDisplay()}` : null}</TableCell><TableCell className="text-right num">{usd(r.igtf_usd)}</TableCell><TableCell /></TableRow> : null}
          {r.vueltos ? (
            <TableRow data-testid="cierre-vueltos">
              <TableCell>Vueltos entregados<span className="block text-[11px] text-muted-foreground">los de pago móvil y transferencia salen del banco, no de la caja</span></TableCell>
              <TableCell />
              <TableCell className="text-right num">
                {usd(r.vueltos.usd)} · Bs {Money.parse(r.vueltos.bs_efectivo).toDisplay()} en efectivo
                {Money.parse(r.vueltos.bs_pago_movil).esPositivo() ? <span className="block">Bs {Money.parse(r.vueltos.bs_pago_movil).toDisplay()} por pago móvil</span> : null}
                {Money.parse(r.vueltos.bs_transferencia).esPositivo() ? <span className="block">Bs {Money.parse(r.vueltos.bs_transferencia).toDisplay()} por transferencia</span> : null}
              </TableCell>
              <TableCell />
            </TableRow>
          ) : null}
          {r.sobrante_usd && Money.parse(r.sobrante_usd).esPositivo() ? (
            <TableRow data-testid="cierre-sobrante"><TableCell>Sobrante por vuelto ({r.vueltos?.cobros_con_sobrante ?? 0} cobro(s)): quedó en caja</TableCell><TableCell /><TableCell className="text-right num">{usd(r.sobrante_usd)}</TableCell><TableCell /></TableRow>
          ) : null}
          {r.tipo === 'completo' ? <TableRow><TableCell>Abonos a saldo (incluidos)</TableCell><TableCell /><TableCell className="text-right num">{usd(r.abonos_usd)}</TableCell><TableCell /></TableRow> : null}
          {r.tipo === 'completo' && r.retiros_usd && Money.parse(r.retiros_usd).esPositivo() ? <TableRow data-testid="reporte-retiros"><TableCell>Retiros parciales de efectivo<span className="block text-[11px] text-muted-foreground">no son un gasto: ya se entregaron y no se cuentan</span></TableCell><TableCell /><TableCell className="text-right num">−{usd(r.retiros_usd)}</TableCell><TableCell /></TableRow> : null}
          {r.tipo === 'completo' && (Money.parse(r.egresos_usd).esPositivo() || Money.parse(r.reintegros_usd ?? '0').esPositivo()) ? <TableRow><TableCell>Salidas de caja anteriores al 09/10/2026 (egresos · reintegros)</TableCell><TableCell /><TableCell className="text-right num">−{usd(r.egresos_usd)} · −{usd(r.reintegros_usd ?? '0')}</TableCell><TableCell /></TableRow> : null}
        </TableBody>
      </Table>
      <div className="flex flex-wrap gap-2 text-xs">
        {r.correlativos.map((c) => (
          <span key={c.tipo} className="rounded-md border px-2 py-1" data-testid={`correlativos-${r.tipo}-${c.tipo}`}>
            <b>{c.tipo === 'CTRL' ? 'N° de control' : c.tipo}</b> {c.cantidad === 0 ? 'sin emisiones' : `${c.desde} → ${c.hasta} (${c.cantidad})`}
            {c.saltos.length > 0 ? <Badge variant="critico" className="ml-1">SALTOS: {c.saltos.join(', ')}</Badge> : c.cantidad > 0 ? <Badge variant="ok" className="ml-1">sin saltos</Badge> : null}
            {c.anulados?.length ? <Badge variant="pendiente" className="ml-1" title="Anuladas por hoja dañada: cuentan en el consecutivo y valen cero">anuladas: {c.anulados.join(', ')}</Badge> : null}
            {c.notas?.length ? <Badge variant="neutro" className="ml-1" title="El turno pasó de un talonario al siguiente: no son hojas perdidas" data-testid={`correlativos-notas-${r.tipo}`}>{c.notas.join(' · ')}</Badge> : null}
          </span>
        ))}
        {tasaBs && veTasa ? <span className="text-muted-foreground">tasa 2 al cierre {formatearTasa(tasaBs)}</span> : null}
      </div>
    </div>
  );
}

type Lote = { id: string; numero: string; monto: string };
const loteVacio = (): Lote => ({ id: crypto.randomUUID(), numero: '', monto: '' });

/** Paso 1: lo que se cuenta, sin ningún monto del sistema. */
function ConteoCiego({ c, alDeclarar }: { c: ConteoCajaDTO; alDeclarar: (r: CierreSesionDTO) => void }) {
  const [contado, setContado] = useState<Record<string, string>>({});
  const [lotes, setLotes] = useState<Lote[]>(() => [loteVacio()]);
  const [observacion, setObservacion] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const metodos = c.metodos.filter((m) => m.metodo !== 'punto');
  const conPunto = c.metodos.some((m) => m.metodo === 'punto');
  const faltan = metodos.filter((m) => !leerMonto(contado[m.metodo] ?? '')).map((m) => m.metodo);
  // Un lote a medio llenar no se envía: o tiene número y monto, o está vacío
  const lotesLlenos = lotes.filter((l) => l.numero.trim() || l.monto.trim());
  const lotesMal = lotesLlenos.filter((l) => !l.numero.trim() || !leerMonto(l.monto));
  const totalPunto = Money.sumar(lotesLlenos.map((l) => leerMonto(l.monto) ?? Money.cero()));
  const listo = faltan.length === 0 && lotesMal.length === 0;
  const declarar = useMutation({
    mutationFn: () =>
      post<CierreSesionDTO>(`/cierres/${c.sesion_id}/declarar`, {
        conteo: metodos.map((m) => ({ metodo: m.metodo, monto: leerMonto(contado[m.metodo] ?? '')!.toDocumento() })),
        lotes_punto: lotesLlenos.map((l) => ({ numero: l.numero.trim(), monto: leerMonto(l.monto)!.toDocumento() })),
        ...(observacion.trim() ? { observacion: observacion.trim() } : {}),
      }),
    onSuccess: alDeclarar,
    onError: (e) => { setConfirmando(false); toast.error('No se pudo declarar el conteo', { description: mensajeDeError(e) }); },
  });
  return (
    <div className="space-y-4" data-testid="conteo-ciego">
      <Card>
        <CardHeader>
          <CardTitle>Cuenta lo que tienes y escríbelo</CardTitle>
          <CardDescription>
            Escribe <b>todo lo que contaste</b> en cada método; si no hubo, escribe 0. En efectivo cuenta también el fondo de la caja ($ {Money.parse(c.fondo_usd).toDisplay()} · Bs {Money.parse(c.fondo_bs).toDisplay()}). Los retiros que ya entregaste no se cuentan. Al declarar queda guardado y no se puede cambiar: después verás si cuadra.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {metodos.map((m) => (
              <Campo key={m.metodo} etiqueta={`${etiqueta(m.metodo)} (${simbolo(m.moneda)})`} htmlFor={`conteo-${m.metodo}`} ayuda={Money.parse(m.fondo).esPositivo() ? `incluye el fondo de ${simbolo(m.moneda)} ${Money.parse(m.fondo).toDisplay()}` : m.metodo === 'transferencia' || m.metodo === 'pago_movil' ? 'suma de lo recibido en el turno' : 'lo recibido en el turno'}>
                <Input id={`conteo-${m.metodo}`} inputMode="decimal" placeholder="0,00" value={contado[m.metodo] ?? ''} onChange={(e) => { setConfirmando(false); setContado((d) => ({ ...d, [m.metodo]: e.target.value })); }} className="text-right" data-testid={`conteo-${m.metodo}`} />
              </Campo>
            ))}
          </div>
          {conPunto ? (
            <div className="rounded-lg border p-3" data-testid="conteo-lotes">
              <p className="text-sm font-medium">Punto de venta{c.punto_banco ? ` · ${c.punto_banco}` : ''}: cierre de lote</p>
              <p className="mb-2 text-xs text-muted-foreground">Un renglón por cada lote que cerraste en el punto: su número y su monto en Bs. Si no pasaste tarjetas, déjalo vacío.</p>
              <div className="space-y-2">
                {lotes.map((l, i) => (
                  <div key={l.id} className="flex items-center gap-2">
                    <Input placeholder="N° de lote" value={l.numero} onChange={(e) => { setConfirmando(false); setLotes((ls) => ls.map((x, j) => (j === i ? { ...x, numero: e.target.value } : x))); }} className="w-40" aria-label={`Número del lote ${i + 1}`} data-testid={`lote-numero-${i}`} />
                    <Input placeholder="Monto Bs" inputMode="decimal" value={l.monto} onChange={(e) => { setConfirmando(false); setLotes((ls) => ls.map((x, j) => (j === i ? { ...x, monto: e.target.value } : x))); }} className="w-40 text-right" aria-label={`Monto del lote ${i + 1}`} data-testid={`lote-monto-${i}`} />
                    {lotes.length > 1 ? <Button size="sm" variant="ghost" onClick={() => setLotes((ls) => ls.filter((_, j) => j !== i))} aria-label={`Quitar el lote ${i + 1}`}><Trash2 /></Button> : null}
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <Button size="sm" variant="outline" onClick={() => setLotes((ls) => [...ls, loteVacio()])} data-testid="lote-agregar"><Plus /> Otro lote</Button>
                <span className="text-sm">Total del punto: <b className="num" data-testid="lote-total">Bs {totalPunto.toDisplay()}</b></span>
              </div>
              {lotesMal.length > 0 ? <p className="mt-1 text-xs text-mg-ambar">Cada lote lleva su número y su monto.</p> : null}
            </div>
          ) : null}
          {c.retiros.length > 0 ? (
            <div className="rounded-lg bg-muted/60 p-3 text-sm" data-testid="conteo-retiros">
              <p className="font-medium">Retiros de este turno (ya entregados: no los cuentes)</p>
              <ul className="mt-1 space-y-0.5">
                {c.retiros.map((r) => <li key={r.id} className="num">{r.numero_formateado} · {simbolo(r.moneda)} {Money.parse(r.monto).toDisplay()} · a {r.recibe} · {r.estado === 'por_confirmar' ? 'por confirmar' : r.estado}</li>)}
              </ul>
            </div>
          ) : null}
          <Campo etiqueta="Observación (opcional)" htmlFor="conteo-observacion">
            <Input id="conteo-observacion" value={observacion} onChange={(e) => setObservacion(e.target.value)} maxLength={500} />
          </Campo>
          {c.esperas_pendientes > 0 ? <p className="text-xs text-muted-foreground">{c.esperas_pendientes} documento(s) en espera se descartarán al cerrar.</p> : null}
        </CardContent>
      </Card>
      <DialogFooter>
        {confirmando ? (
          <>
            <span className="mr-auto self-center text-sm text-mg-ambar" data-testid="conteo-aviso">Revisa lo escrito: después de declarar no se puede cambiar.</span>
            <Button variant="outline" onClick={() => setConfirmando(false)}>Corregir</Button>
            <Button cargando={declarar.isPending} onClick={() => declarar.mutate()} data-testid="conteo-confirmar"><Lock /> Sí, declarar</Button>
          </>
        ) : (
          <>
            {faltan.length > 0 ? <span className="mr-auto self-center text-xs text-muted-foreground" data-testid="conteo-faltan">Falta escribir: {faltan.map(etiqueta).join(', ')} (0 si no hubo).</span> : null}
            <Button disabled={!listo} onClick={() => setConfirmando(true)} data-testid="conteo-declarar"><Lock /> Declarar conteo</Button>
          </>
        )}
      </DialogFooter>
    </div>
  );
}

/** Arqueo con lo declarado frente al sistema. Solo se muestra cuando ya hay conteo declarado o a quien autoriza cierres. */
function TablaArqueo({ p }: { p: CierreSesionDTO }) {
  const a = p.arqueo;
  // Con qué tasa se pasan los Bs a $: solo la dueña (pedido de la dueña 2026-10-19)
  const veTasa = useVeTasa2();
  const diff = a.diferencia_usd ? Money.parse(a.diferencia_usd) : null;
  const difAbs = a.diferencia_abs_usd ? Money.parse(a.diferencia_abs_usd) : null;
  return (
    <Card data-testid="arqueo">
      <CardHeader>
        <CardTitle>Arqueo por método</CardTitle>
        <CardDescription>
          Lo contado por el cajero frente a lo que espera el sistema. En efectivo, el sistema parte del fondo ($ {Money.parse(a.fondo_usd).toDisplay()} · Bs {Money.parse(a.fondo_bs).toDisplay()}) y ya descuenta vueltos y retiros parciales. La diferencia total se expresa en {veTasa ? '$ (Bs a la tasa 2 del cierre)' : '$'}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Table data-testid="arqueo-tabla">
          <TableHeader>
            <TableRow><TableHead>Método</TableHead><TableHead className="text-right">Sistema</TableHead><TableHead className="text-right">Cajero</TableHead><TableHead className="text-right">Diferencia</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {filasArqueo(a).map((f) => {
              const dif = f.diferencia ? Money.parse(f.diferencia) : null;
              return (
                <TableRow key={f.metodo} data-testid={`arqueo-fila-${f.metodo}`}>
                  <TableCell>
                    <p className="font-medium">{etiqueta(f.metodo)}</p>
                    {Money.parse(f.fondo).esPositivo() ? <p className="text-[11px] text-muted-foreground">incluye el fondo de {simbolo(f.moneda)} {Money.parse(f.fondo).toDisplay()}</p> : null}
                    {f.metodo === 'punto' && (a.lotes_punto?.length ?? 0) > 0 ? <p className="text-[11px] text-muted-foreground" data-testid="arqueo-lotes">{a.punto_banco ? `${a.punto_banco} · ` : ''}lotes: {a.lotes_punto!.map((l) => `${l.numero} (Bs ${Money.parse(l.monto).toDisplay()})`).join(', ')}</p> : null}
                  </TableCell>
                  <TableCell className="text-right num"><span data-testid={`arqueo-sistema-${f.metodo}`} data-valor={f.sistema}>{simbolo(f.moneda)} {Money.parse(f.sistema).toDisplay()}</span></TableCell>
                  <TableCell className="text-right num" data-testid={`arqueo-declarado-${f.metodo}`}>{f.declarado ? `${simbolo(f.moneda)} ${Money.parse(f.declarado).toDisplay()}` : '—'}</TableCell>
                  <TableCell className={`text-right num ${dif && !dif.esCero() ? 'font-semibold text-mg-rojo' : ''}`} data-testid={`arqueo-dif-${f.metodo}`}>
                    {dif ? `${simbolo(f.moneda)} ${dif.toDisplay()}` : '—'}
                    {dif && f.moneda === 'BS' && f.diferencia_usd && !dif.esCero() ? <p className="text-[11px] font-normal text-muted-foreground">≈ $ {Money.parse(f.diferencia_usd).toDisplay()}</p> : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {a.otros.length > 0 ? (
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground" data-testid="arqueo-verificables">
            <span>Verificables por banco (no se cuentan):</span>
            {a.otros.map((o) => <span key={o.metodo} className="rounded-md border px-2 py-0.5">{etiqueta(o.metodo)}: <b className="num">{simbolo(o.moneda)} {Money.parse(o.sistema).toDisplay()}</b></span>)}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-muted/60 p-3">
          <span className="text-sm">Diferencia total (cajero − sistema):</span>
          <b className={`num text-lg ${diff && !diff.esCero() ? 'text-mg-rojo' : 'text-mg-verde'}`} data-testid="arqueo-diferencia">{diff ? usd(diff.toDocumento()) : '—'}</b>
          {difAbs && diff && !difAbs.eq(diff.abs()) ? <span className="text-xs text-muted-foreground" data-testid="arqueo-diferencia-abs">por método suman {usd(difAbs.toDocumento())}</span> : null}
          {a.requiere_aprobacion ? <Badge variant="critico" data-testid="arqueo-supera-umbral">supera el umbral de $ {Money.parse(a.umbral_usd).toDisplay()}: requiere autorización</Badge> : null}
          {p.justificacion_diferencia ? <span className="text-sm text-muted-foreground">Justificación: {p.justificacion_diferencia}{p.aprobado_por_nombre ? ` · autorizó ${p.aprobado_por_nombre}` : ''}</span> : null}
        </div>
        {/* Lo que se escribió al declarar: antes se guardaba y nadie lo leía (re-auditoría 2026-10-18, ronda 2, hallazgo 5-H5) */}
        {p.observacion ? <p className="text-sm" data-testid="arqueo-observacion"><span className="text-muted-foreground">Observación del conteo:</span> {p.observacion}</p> : null}
      </CardContent>
    </Card>
  );
}

function Retiros({ p }: { p: CierreSesionDTO }) {
  if (!p.retiros || p.retiros.length === 0) return null;
  return (
    <Card data-testid="cierre-retiros">
      <CardHeader><CardTitle>Retiros parciales de efectivo</CardTitle><CardDescription>Efectivo entregado durante el turno. No es un gasto: el arqueo ya lo descuenta de lo que espera en caja.</CardDescription></CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Retiro</TableHead><TableHead>Hora</TableHead><TableHead className="text-right">Monto</TableHead><TableHead>Entregado a</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
          <TableBody>
            {p.retiros.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.numero_formateado}</TableCell>
                <TableCell className="text-xs">{formatearFechaHora(r.registrado_en)}</TableCell>
                <TableCell className="text-right num">{simbolo(r.moneda)} {Money.parse(r.monto).toDisplay()}</TableCell>
                <TableCell>{r.recibe}</TableCell>
                <TableCell><Badge variant={r.estado === 'confirmado' ? 'ok' : r.estado === 'observado' ? 'critico' : 'pendiente'}>{r.estado === 'por_confirmar' ? 'Por confirmar' : r.estado === 'confirmado' ? `Confirmó ${r.confirmado_por_nombre ?? ''}` : 'Observado'}</Badge>{r.comentario ? <span className="block text-[11px] text-muted-foreground">{r.comentario}</span> : null}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function Documentos({ p }: { p: CierreSesionDTO }) {
  const [abierto, setAbierto] = useState(false);
  if (!p.documentos || p.documentos.length === 0) return null;
  return (
    <Card data-testid="cierre-documentos">
      <CardHeader className="flex-row items-center justify-between">
        <div><CardTitle>Documentos del turno por método de pago</CardTitle><CardDescription>{p.documentos.length} documento(s): lo que se cobró de cada uno en esta sesión.</CardDescription></div>
        <Button size="sm" variant="outline" onClick={() => setAbierto((v) => !v)} data-testid="cierre-documentos-ver">{abierto ? 'Ocultar' : 'Ver detalle'}</Button>
      </CardHeader>
      {abierto ? (
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Documento</TableHead><TableHead>Cliente</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Cobrado en esta sesión</TableHead></TableRow></TableHeader>
            <TableBody>
              {p.documentos.map((d) => (
                <TableRow key={d.id} className={d.estado === 'anulado' ? 'text-muted-foreground' : undefined}>
                  <TableCell><Badge variant="neutro">{d.tipo}</Badge> <b className={d.estado === 'anulado' ? 'line-through' : undefined}>{d.numero_formateado}</b>{d.de_otra_sesion ? <span className="block text-[11px] text-muted-foreground">de otro turno: aquí se le cobró</span> : null}</TableCell>
                  <TableCell>{d.cliente_razon_social}</TableCell>
                  <TableCell className="text-right num">{usd(d.total)}</TableCell>
                  <TableCell className="text-xs">{d.pagos.length === 0 ? <span className="text-muted-foreground">sin cobro en este turno</span> : d.pagos.map((x) => `${etiqueta(x.metodo)} ${usd(x.monto_usd)}${x.referencias ? ` (ref. ${x.referencias})` : ''}`).join(' · ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      ) : null}
    </Card>
  );
}

/**
 * Abonos del turno (respuesta de la dueña del 26-10-2026): cada abono a saldo con su hora, cliente, recibo, forma de
 * pago, referencia y monto, para cotejar el Zelle y el efectivo y saber a quién se acreditó. El reporte COMPLETO solo
 * trae la suma.
 */
function AbonosDelTurno({ p }: { p: CierreSesionDTO }) {
  if (!p.abonos || p.abonos.length === 0) return null;
  const total = Money.sumar(p.abonos.map((a) => Money.parse(a.total_usd)));
  return (
    <Card data-testid="cierre-abonos">
      <CardHeader><CardTitle>Abonos del turno</CardTitle><CardDescription>{p.abonos.length} abono(s) a saldo a favor por {usd(total.toDocumento())}: ya van incluidos en el reporte completo.</CardDescription></CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Hora</TableHead><TableHead>Cliente</TableHead><TableHead>Recibo</TableHead><TableHead>Forma de pago</TableHead><TableHead className="text-right">Monto</TableHead></TableRow></TableHeader>
          <TableBody>
            {p.abonos.map((a) => (
              <TableRow key={a.id} data-testid={`cierre-abono-${a.recibo}`}>
                <TableCell className="whitespace-nowrap">{formatearFechaHora(a.recibido_en)}</TableCell>
                <TableCell>{a.cliente_razon_social}</TableCell>
                <TableCell><a href={`/imprimir/ABONO/${a.id}`} target="_blank" rel="noreferrer" className="underline">{a.recibo}</a></TableCell>
                <TableCell className="text-xs">{a.pagos.map((x) => `${etiqueta(x.metodo)} ${x.moneda === 'BS' ? `Bs ${Money.parse(x.monto).toDisplay()}` : usd(x.monto)}${x.referencia ? ` (ref. ${x.referencia})` : ''}`).join(' · ')}</TableCell>
                <TableCell className="text-right num">{usd(a.total_usd)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function CierreDialog({ sesionId, cerrar, alCerrar }: { sesionId: string | null; cerrar: () => void; alCerrar: () => void }) {
  return (
    <Dialog open={!!sesionId} onOpenChange={(o) => { if (!o) cerrar(); }}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="dialogo-cierre">
        {/* El contenido se monta por sesión: su estado (lo escrito en el conteo) nace limpio en cada apertura */}
        {sesionId ? <Contenido key={sesionId} sesionId={sesionId} alCerrar={alCerrar} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function Contenido({ sesionId, alCerrar }: { sesionId: string; alCerrar: () => void }) {
  const { usuario, puede } = useAuth();
  const qc = useQueryClient();
  const aut = useAutorizacion();
  const [just, setJust] = useState('');
  const [motivoReconteo, setMotivoReconteo] = useState('');
  const [pidiendoReconteo, setPidiendoReconteo] = useState(false);
  /** Quien autoriza cierres abre el conteo de la caja de otro (p. ej. el cajero se fue sin cerrar). */
  const [contarYo, setContarYo] = useState(false);

  // Lo que se cuenta (sin montos): lo ve quien opera el cierre. A otro cajero le responde 403.
  const conteo = useQuery({ queryKey: ['cierres', 'conteo', sesionId], queryFn: () => get<ConteoCajaDTO>(`/cierres/${sesionId}/conteo`), retry: false });
  const esMia = !!conteo.data && conteo.data.usuario_nombre === usuario?.nombre;
  /** Autoriza cierres: tiene el permiso y la caja no es la suya (la dueña, siempre). */
  const autorizo = usuario?.rol === 'superadmin' || (puede('caja.cerrar-con-diferencia') && !esMia);
  const abierta = conteo.data?.estado === 'abierta';
  const declarado = conteo.data?.declarado ?? false;
  // Reportes y arqueo: cuando la caja ya declaró o está cerrada; antes, solo quien autoriza cierres
  const veCierre = conteo.data ? !abierta || declarado || autorizo : conteo.isError;
  const cierre = useQuery({ queryKey: ['cierres', 'detalle', sesionId], queryFn: () => get<CierreSesionDTO>(`/cierres/${sesionId}`), enabled: veCierre, retry: false });

  const refrescar = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ['cierres'] }), qc.invalidateQueries({ queryKey: ['caja'] })]);
    alCerrar();
  };
  const alDeclarar = (r: CierreSesionDTO) => {
    if (r.estado === 'cerrada') mostrarRegistrado({ titulo: `${r.caja_nombre} cerrada`, descripcion: 'El conteo cuadra con lo que registró el sistema.', detalle: r.esperas_purgadas > 0 ? [{ texto: `Se descartaron ${r.esperas_purgadas} venta(s) en espera.` }] : [], imprimir: [{ etiqueta: 'Imprimir reportes', ruta: `/imprimir/CIERRE/${sesionId}` }] });
    else toast.warning('Conteo declarado: hay diferencias', { description: 'Revísalas y justifícalas para cerrar la caja.' });
    setContarYo(false);
    void refrescar();
  };
  const cerrarM = useMutation({
    mutationFn: async () => {
      const p = cierre.data!;
      return aut.ejecutar(
        (autorizaciones) => post<CierreSesionDTO>(`/cierres/${sesionId}/cerrar`, { justificacion_diferencia: just.trim() || undefined, autorizaciones }),
        { motivo: just.trim(), detalle: { caja: p.caja_nombre, cajero: p.usuario_nombre, diferencia: usd(p.arqueo.diferencia_usd) }, ...(p.arqueo.diferencia_abs_usd ? { montoUsd: p.arqueo.diferencia_abs_usd } : {}) },
      );
    },
    onSuccess: (r) => {
      if (!r) return; // canceló la autorización: el cierre sigue en pantalla
      mostrarRegistrado({ titulo: `${r.caja_nombre} cerrada`, descripcion: `Cerrada con una diferencia de ${usd(r.arqueo.diferencia_usd)}, ya justificada.`, detalle: r.esperas_purgadas > 0 ? [{ texto: `Se descartaron ${r.esperas_purgadas} venta(s) en espera.` }] : [], imprimir: [{ etiqueta: 'Imprimir reportes', ruta: `/imprimir/CIERRE/${sesionId}` }] });
      void refrescar();
    },
    onError: (e) => toast.error('No se pudo cerrar la caja', { description: mensajeDeError(e) }),
  });
  const reconteoM = useMutation({
    mutationFn: () => post<ConteoCajaDTO>(`/cierres/${sesionId}/reconteo`, { motivo: motivoReconteo.trim() }),
    onSuccess: () => { toast.success('El cajero puede volver a contar', { description: 'Quedó registrado y le llega el aviso.' }); setPidiendoReconteo(false); setMotivoReconteo(''); void refrescar(); },
    onError: (e) => toast.error('No se pudo permitir el reconteo', { description: mensajeDeError(e) }),
  });

  if (conteo.isLoading) return <p className="p-6 text-sm text-muted-foreground">Cargando…</p>;
  const p = cierre.data;
  const c = conteo.data;
  const titulo = c ? `${c.caja_nombre} · ${c.usuario_nombre}` : p ? `${p.caja_nombre} · ${p.usuario_nombre}` : 'Cierre de caja';
  const turno = c?.turno ?? p?.turno;
  const hayDiferencia = !!p && p.estado === 'abierta' && !!p.declarado && filasArqueo(p.arqueo).some((f) => f.diferencia !== null && !Money.parse(f.diferencia).esCero());
  const enConteo = !!c && abierta && !declarado && (esMia || contarYo);
  const ciego = cierre.error instanceof ErrorApi && cierre.error.status === 403;
  const noEsDeAqui = [conteo.error, cierre.error].some((e) => e instanceof ErrorApi && e.status === 404);

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-1">
          <DialogTitle>
            {/* Sin datos (no se pudo leer la caja) no se inventa un estado: solo el título */}
            {c || p ? (
              <>
                {abierta ? 'Cerrar' : 'Cierre'} {titulo} {turno ? <Badge variant="neutro">turno {turno}</Badge> : null}{' '}
                <Badge variant={abierta ? (declarado ? 'pendiente' : 'ok') : 'neutro'} data-testid="cierre-estado">{abierta ? (declarado ? 'conteo declarado' : 'abierta') : 'cerrada'}</Badge>
              </>
            ) : (
              titulo
            )}
          </DialogTitle>
          {/* La ayuda del paso en que está el cierre: contar, justificar la diferencia o leer los reportes */}
          {enConteo ? <AyudaVentana tema="cierres.conteo" /> : hayDiferencia ? <AyudaVentana tema="cierres.diferencia" /> : <AyudaVentana tema="cierres.reporte" />}
        </div>
        <DialogDescription>
          {c ? `Abierta ${formatearFechaHora(c.abierta_en)}` : p ? `Abierta ${formatearFechaHora(p.abierta_en)}` : ''}
          {p?.cerrada_en ? ` · cerrada ${formatearFechaHora(p.cerrada_en)} por ${p.cerrada_por_nombre}` : ''}
          {p && p.reaperturas > 0 ? ` · reabierta ${p.reaperturas} vez` : ''}
          {p && (p.reconteos ?? 0) > 0 ? ` · se volvió a contar ${p.reconteos} vez` : ''}
        </DialogDescription>
      </DialogHeader>

      {enConteo && c ? (
        <ConteoCiego c={c} alDeclarar={alDeclarar} />
      ) : !c && !p ? (
        // Solo el 403 del conteo a ciegas significa «todavía no declara». Cualquier otro error se dice tal cual: antes
        // todos salían con ese texto, también cuando la caja ya había declarado (reporte del 11-10-2026)
        cierre.isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : ciego ? (
          <p className="text-sm text-muted-foreground" data-testid="cierre-sin-acceso">Esta caja está abierta y todavía no declara su conteo: su cierre se ve cuando el cajero cuente.</p>
        ) : (
          <p className="text-sm text-mg-rojo" role="alert" data-testid="cierre-error">
            No se pudo abrir el cierre de esta caja: {mensajeDeError(cierre.error ?? conteo.error)}.
            {noEsDeAqui ? ' Esa caja no es de la sucursal que estás operando: cambia de sucursal para contarla y cerrarla.' : ''}
          </p>
        )
      ) : cierre.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Calculando…</p>
      ) : p ? (
        <>
          {p.estado === 'abierta' && !p.declarado ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="cierre-en-vivo">
              <span>Vista en vivo de una caja abierta. El cajero no ve estos montos: cuenta a ciegas.</span>
              {autorizo && c ? <Button size="sm" variant="outline" onClick={() => setContarYo(true)} data-testid="cierre-contar-yo">Contar y cerrar esta caja</Button> : null}
            </div>
          ) : null}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card><CardHeader><CardTitle>Reporte FISCAL</CardTitle><CardDescription>Solo FAC / NC / ND</CardDescription></CardHeader><CardContent><Reporte r={p.fiscal} /></CardContent></Card>
            <Card><CardHeader><CardTitle>Reporte COMPLETO</CardTitle><CardDescription>FAC + NOTA + abonos</CardDescription></CardHeader><CardContent><Reporte r={p.completo} tasaBs={p.arqueo.tasa_paralela_cierre} /></CardContent></Card>
          </div>
          <TablaArqueo p={p} />
          <Retiros p={p} />
          <AbonosDelTurno p={p} />
          <Documentos p={p} />
          {hayDiferencia ? (
            <Card data-testid="cierre-paso2">
              <CardHeader><CardTitle>El conteo no cuadra</CardTitle><CardDescription>Lo declarado ya quedó guardado. Para cerrar, justifica la diferencia{p.arqueo.requiere_aprobacion ? ' y pide la autorización de un administrador o de la dueña' : ''}. Si te equivocaste al escribir, quien autoriza los cierres puede permitirte contar de nuevo.</CardDescription></CardHeader>
              <CardContent>
                <Campo etiqueta="Justificación de la diferencia (obligatoria)" htmlFor="just">
                  <Textarea id="just" rows={2} value={just} onChange={(e) => setJust(e.target.value)} data-testid="arqueo-justificacion" />
                </Campo>
                {pidiendoReconteo ? (
                  <div className="mt-3 flex items-end gap-2" data-testid="reconteo-form">
                    <Campo etiqueta="Motivo para volver a contar" htmlFor="reconteo-motivo" className="flex-1"><Input id="reconteo-motivo" value={motivoReconteo} onChange={(e) => setMotivoReconteo(e.target.value)} data-testid="reconteo-motivo" /></Campo>
                    <Button variant="outline" onClick={() => setPidiendoReconteo(false)}>Cancelar</Button>
                    <Button disabled={motivoReconteo.trim().length < 5} cargando={reconteoM.isPending} onClick={() => reconteoM.mutate()} data-testid="reconteo-confirmar">Permitir</Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => window.open(`/imprimir/CIERRE/${sesionId}`, '_blank')} data-testid="cierre-imprimir"><Printer /> Imprimir reportes</Button>
            {hayDiferencia && autorizo && !pidiendoReconteo ? <Button variant="outline" onClick={() => setPidiendoReconteo(true)} data-testid="reconteo-abrir"><RotateCcw /> Permitir volver a contar</Button> : null}
            {hayDiferencia && (esMia || puede('caja.cerrar-con-diferencia')) ? <Button cargando={cerrarM.isPending} disabled={just.trim().length < 3} onClick={() => cerrarM.mutate()} data-testid="confirmar-cierre-caja"><Lock /> Cerrar caja con la diferencia</Button> : null}
          </DialogFooter>
        </>
      ) : (
        <p className="text-sm text-mg-rojo">{mensajeDeError(cierre.error ?? conteo.error)}</p>
      )}
      {aut.dialogo}
    </>
  );
}
